"use client";

import { useMemo, useState } from "react";
import { X, Wrench } from "lucide-react";
import { SlideDrawer } from "@/components/ui/slide-drawer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { capabilityColors } from "@/lib/capability-colors";
import { CapabilityListPanel } from "@/components/capability-list/CapabilityListPanel";
import type { CapabilityVisibility } from "@/types/capability-types";
import { normalizeCapabilityVisibility } from "@/lib/capability-visibility";

export interface ToolCapabilityOption {
    name: string;
    description: string | null;
    tags?: string[];
    app_key?: string;
    visibility?: string;
    created_by?: string;
    organization_id?: string;
    access_source?: string;
}

export function toolCapabilityRef(tool: Pick<ToolCapabilityOption, "name" | "app_key">): string {
    return tool.app_key ? `${tool.app_key}/${tool.name}` : tool.name;
}

const SORT_OPTIONS = [
    { key: "name", label: "Name" },
    { key: "description", label: "Description" },
];

export type VisibilityScope = CapabilityVisibility | "all";

interface ToolCapabilityPickerDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    tools: ToolCapabilityOption[];
    selectedToolRef: string;
    onSelect: (toolName: string) => void;
    loading?: boolean;
    currentUserId?: string;
    currentOrgId?: string;
}

const VISIBILITY_SCOPE_OPTIONS: { key: VisibilityScope; label: string }[] = [
    { key: "all", label: "All" },
    { key: "user", label: "User" },
    { key: "organization", label: "Organization" },
    { key: "application", label: "Application" },
    { key: "account", label: "Account" },
    { key: "global", label: "Global" },
];

export function ToolCapabilityPickerDrawer({
    isOpen,
    onClose,
    tools,
    selectedToolRef,
    onSelect,
    loading = false,
    currentUserId: _currentUserId = "",
    currentOrgId: _currentOrgId = "",
}: ToolCapabilityPickerDrawerProps) {
    const [searchQuery, setSearchQuery] = useState("");
    const [sortField, setSortField] = useState<string>("name");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
    const [visibilityScope, setVisibilityScope] = useState<VisibilityScope>("all");

    const allTags = useMemo(() => {
        const tagSet = new Set<string>();
        for (const t of tools) {
            if (t.tags) {
                for (const tag of t.tags) tagSet.add(tag);
            }
        }
        return Array.from(tagSet).sort();
    }, [tools]);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const filteredTools = useMemo(() => {
        let list = tools.filter((t) => {
            if (
                visibilityScope !== "all" &&
                t.access_source !== "entitled" &&
                normalizeCapabilityVisibility(t.visibility) !== visibilityScope
            ) {
                return false;
            }
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                if (
                    !t.name.toLowerCase().includes(q) &&
                    !(t.description?.toLowerCase().includes(q) ?? false)
                ) {
                    return false;
                }
            }
            if (selectedTags.size > 0 && t.tags) {
                if (!t.tags.some((tag) => selectedTags.has(tag))) return false;
            } else if (selectedTags.size > 0 && !t.tags?.length) {
                return false;
            }
            return true;
        });
        const cmp = (a: ToolCapabilityOption, b: ToolCapabilityOption) => {
            if (sortField === "name") return a.name.localeCompare(b.name);
            const ad = (a.description ?? "").toLowerCase();
            const bd = (b.description ?? "").toLowerCase();
            return ad.localeCompare(bd);
        };
        list = [...list].sort((a, b) => {
            const r = cmp(a, b);
            return sortDirection === "asc" ? r : -r;
        });
        return list;
    }, [tools, searchQuery, selectedTags, sortField, sortDirection, visibilityScope]);

    const showEmptySource = !loading && tools.length === 0;
    const colors = capabilityColors.tool;

    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 shrink-0">
                            <div className="flex items-center gap-3">
                                <div
                                    className={cn(
                                        "flex h-10 w-10 items-center justify-center rounded-lg",
                                        colors.bgLight,
                                        colors.text
                                    )}
                                >
                                    <Wrench size={20} />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900">
                                        Select Tool Capability
                                    </h2>
                                    <p className="text-sm text-slate-500">
                                        Choose the tool capability for this integration
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onClose}
                                className="h-8 w-8 p-0"
                            >
                                <X size={16} />
                            </Button>
                        </div>

                        {showEmptySource ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center flex-1">
                                <Wrench size={32} className="text-slate-300 mb-3" />
                                <p className="text-sm text-slate-500">
                                    No tool capabilities available
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                    Create a tool in this application, or subscribe to one in the marketplace
                                </p>
                            </div>
                        ) : (
                            <CapabilityListPanel<ToolCapabilityOption>
                                items={filteredTools}
                                sortOptions={SORT_OPTIONS}
                                sortField={sortField}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                                searchQuery={searchQuery}
                                onSearchChange={setSearchQuery}
                                searchPlaceholder="Search tool capabilities by name or description..."
                                allTags={allTags}
                                selectedTags={selectedTags}
                                onToggleTag={(tag) => {
                                    setSelectedTags((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(tag)) next.delete(tag);
                                        else next.add(tag);
                                        return next;
                                    });
                                }}
                                onClearAllTags={() => setSelectedTags(new Set())}
                                onSelectAllTags={() => setSelectedTags(new Set(allTags))}
                                isLoading={loading}
                                loadingMessage="Loading tool capabilities..."
                                emptyMessage="No tool capabilities found."
                                extraToolbar={
                                    <div className="flex items-center rounded-md border border-[hsl(var(--list-controls-border))] bg-[hsl(var(--list-controls-surface))] p-1">
                                        {VISIBILITY_SCOPE_OPTIONS.map((opt) => (
                                            <button
                                                key={opt.key}
                                                type="button"
                                                onClick={() => setVisibilityScope(opt.key)}
                                                className={cn(
                                                    "flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                                                    visibilityScope === opt.key
                                                        ? "bg-[hsl(var(--list-controls-sort-active-bg))] text-[hsl(var(--list-controls-sort-active-fg))] shadow-sm"
                                                        : "text-[hsl(var(--list-controls-muted-foreground))] hover:bg-[hsl(var(--list-controls-surface-hover))] hover:text-[hsl(var(--list-controls-foreground))]"
                                                )}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                }
                                toolbarClassName="px-12 py-4 border-b border-slate-100 shrink-0"
                                listControlsVariant="solid"
                                contentClassName="flex-1 overflow-y-auto px-24 py-6"
                                gridClassName="grid grid-cols-3 gap-3"
                                renderCard={(tool) => {
                                    const ref = toolCapabilityRef(tool);
                                    const isSelected =
                                        selectedToolRef === ref || selectedToolRef === tool.name;
                                    return (
                                        <Card
                                            key={ref}
                                            className={cn(
                                                "relative border-2 p-3 transition-all duration-200 cursor-pointer min-h-[88px]",
                                                "border-l-4",
                                                isSelected
                                                    ? "border-violet-500 bg-violet-50/50 ring-2 ring-violet-200"
                                                    : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md"
                                            )}
                                            style={{
                                                borderLeftColor: isSelected ? undefined : colors.hex,
                                            }}
                                            onClick={() => {
                                                onSelect(ref);
                                                onClose();
                                            }}
                                        >
                                            <div className="flex flex-row items-start gap-3">
                                                <div
                                                    className={cn(
                                                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                                                        colors.bgLight,
                                                        colors.text
                                                    )}
                                                >
                                                    <Wrench size={16} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                                            tool
                                                        </span>
                                                        {tool.access_source === "entitled" && (
                                                            <Badge
                                                                variant="secondary"
                                                                className="h-4 px-1.5 text-[9px] font-medium"
                                                            >
                                                                Subscribed
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <span className="block text-sm font-bold truncate text-slate-900">
                                                        {tool.name}
                                                    </span>
                                                    {tool.app_key && (
                                                        <span className="text-[10px] text-slate-400 truncate block">
                                                            {tool.app_key}
                                                        </span>
                                                    )}
                                                    {tool.description && (
                                                        <span className="text-[10px] text-slate-500 line-clamp-2 leading-tight block mt-0.5">
                                                            {tool.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </Card>
                                    );
                                }}
                            />
                        )}
        </SlideDrawer>
    );
}
